import { useEffect, useRef, useState } from 'react';
import { shopAxios } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: {
                        client_id: string;
                        callback: (response: { credential?: string }) => void;
                        auto_select?: boolean;
                        cancel_on_tap_outside?: boolean;
                        use_fedcm_for_prompt?: boolean;
                    }) => void;
                    renderButton: (
                        parent: HTMLElement,
                        options: {
                            type?: string;
                            theme?: string;
                            size?: string;
                            text?: string;
                            shape?: string;
                            logo_alignment?: string;
                            width?: number;
                        }
                    ) => void;
                };
            };
        };
    }
}

const GIS_SCRIPT_ID = 'google-gsi-client';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

function loadGisScript(): Promise<void> {
    if (window.google?.accounts?.id) return Promise.resolve();

    const existing = document.getElementById(GIS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
        // Cached / already-finished <script>: 'load' will not fire again.
        if (window.google?.accounts?.id) return Promise.resolve();
        return new Promise((resolve, reject) => {
            const onLoad = () => {
                cleanup();
                resolve();
            };
            const onError = () => {
                cleanup();
                reject(new Error('gis_load_failed'));
            };
            const cleanup = () => {
                existing.removeEventListener('load', onLoad);
                existing.removeEventListener('error', onError);
            };
            existing.addEventListener('load', onLoad);
            existing.addEventListener('error', onError);
            // If the script finished between the checks above and listener attach:
            if (window.google?.accounts?.id) {
                cleanup();
                resolve();
            }
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GIS_SCRIPT_ID;
        script.src = GIS_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('gis_load_failed'));
        document.head.appendChild(script);
    });
}

type Props = {
    onCredential: (idToken: string) => Promise<void> | void;
    onError?: (message: string) => void;
    disabled?: boolean;
};

/**
 * Renders Google Identity Services button when Authentication:Google:ClientId is configured.
 * Hidden entirely when Google login is disabled on the server.
 *
 * Two-phase mount: fetch ClientId → render container → then GIS renderButton into the ref.
 * (A single effect that setEnabled + renderButton races on refresh/mobile when the script is cached.)
 */
const GoogleSignInButton = ({ onCredential, onError, disabled }: Props) => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const [clientId, setClientId] = useState<string | null>(null);
    const [ready, setReady] = useState(false);
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const onCredentialRef = useRef(onCredential);
    const onErrorRef = useRef(onError);
    onCredentialRef.current = onCredential;
    onErrorRef.current = onError;

    // Phase 1: public config
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await shopAxios.get<{ enabled?: boolean; clientId?: string }>(
                    '/auth/google-config'
                );
                const id = data?.clientId?.trim() ?? '';
                if (cancelled) return;
                if (!data?.enabled || !id) {
                    setClientId(null);
                    return;
                }
                setClientId(id);
            } catch {
                if (!cancelled) setClientId(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Phase 2: after container is in the DOM (clientId set), paint the official Google button
    useEffect(() => {
        if (!clientId) return;

        let cancelled = false;
        let resizeObserver: ResizeObserver | null = null;

        const paint = async () => {
            try {
                await loadGisScript();
                if (cancelled || !window.google?.accounts?.id) return;

                // Wait until the ref exists (commit after clientId render)
                let host = buttonRef.current;
                for (let i = 0; i < 10 && !host; i++) {
                    await new Promise<void>((r) => requestAnimationFrame(() => r()));
                    host = buttonRef.current;
                }
                if (cancelled || !host) return;

                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: async (response) => {
                        const token = response.credential?.trim();
                        if (!token) {
                            onErrorRef.current?.(
                                tr('error.googleSignIn', 'Google sign-in failed. Please try again.')
                            );
                            return;
                        }
                        try {
                            await onCredentialRef.current(token);
                        } catch {
                            // Caller surfaces Axios errors
                        }
                    },
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });

                const renderInto = (el: HTMLElement) => {
                    const width = Math.min(
                        400,
                        Math.max(240, Math.floor(el.getBoundingClientRect().width) || el.parentElement?.clientWidth || 320)
                    );
                    el.innerHTML = '';
                    window.google!.accounts.id.renderButton(el, {
                        type: 'standard',
                        theme: 'outline',
                        size: 'large',
                        text: 'continue_with',
                        shape: 'pill',
                        logo_alignment: 'left',
                        width,
                    });
                };

                renderInto(host);
                if (!cancelled) setReady(true);

                // Mobile: first paint sometimes has 0 width; re-render when layout settles
                if (typeof ResizeObserver !== 'undefined') {
                    let lastW = host.getBoundingClientRect().width;
                    resizeObserver = new ResizeObserver(() => {
                        const el = buttonRef.current;
                        if (cancelled || !el || !window.google?.accounts?.id) return;
                        const w = el.getBoundingClientRect().width;
                        if (w > 0 && Math.abs(w - lastW) > 8) {
                            lastW = w;
                            renderInto(el);
                        }
                    });
                    resizeObserver.observe(host);
                }
            } catch {
                if (!cancelled) setReady(false);
            }
        };

        void paint();

        return () => {
            cancelled = true;
            resizeObserver?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers via refs
    }, [clientId]);

    if (!clientId) return null;

    return (
        <div className="mt-6 w-full" aria-busy={!ready}>
            <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-gray-500">
                        {tr('page.register.orContinueWith', 'Or continue with')}
                    </span>
                </div>
            </div>
            <div
                ref={buttonRef}
                className={`flex w-full min-h-11 justify-center ${disabled ? 'pointer-events-none opacity-60' : ''}`}
            />
        </div>
    );
};

export default GoogleSignInButton;
